import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiFetch } from "../utils/api";
import { useCatalogCategoriesContext } from "../hooks/useCatalogCategoriesContext";
import CategoryItemList from '../components/CategoryItemList/CategoryItemList';
import WhatWeSellCarousel from '../components/WhatWeSellCarousel/WhatWeSellCarousel';
import { useCartContext } from '../hooks/useCartContext';
import "./Home.css";

const PRODUCT_ICONS = ["water_drop", "science", "lab_research", "experiment", "speed", "healing"];
const VALUE_ICONS = ["verified", "tune", "bolt", "balance"];

const Home = () => {
  const { t, i18n } = useTranslation();
  const { dispatch } = useCatalogCategoriesContext();
  const navigate = useNavigate();
  const { items: cartItems, addItem, removeItem } = useCartContext();
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await apiFetch('/api/public/home', {
          headers: { Locale: i18n.language },
        });
        const data = await res.json();
        if (res.ok) {
          dispatch({ type: 'SET_CATEGORIES', payload: data.categories, locale: i18n.language });
          setFeatured(data.featured);
        }
      } catch {
        // silently fail — navbar will still fetch categories on its own
      }
    };
    load();
  }, [i18n.language, dispatch]);

  return (
    <div>
      <section className="home-hero">
        <h1>{t("home.hero.headline")}</h1>
        <p>{t("home.hero.tagline")}</p>
        <Link to="/contact" className="home-hero-cta">
          {t("nav.contact")}
        </Link>
      </section>

      {featured.length > 0 && (
        <section className="home-featured">
          <h2 className="home-section-title">{t('home.featured.title')}</h2>
          <CategoryItemList
            items={featured}
            onItemClick={(itemId) => navigate(`/catalog/item/${itemId}`)}
            onAdd={({ _id, name, image }) => addItem({ id: _id, name, image })}
            onRemove={removeItem}
            getInCart={(_id) => cartItems.some((i) => i.id === _id)}
          />
        </section>
      )}

      <section className="home-products">
        <h2 className="home-section-title">{t("home.whatWeSell.title")}</h2>
        <p className="home-section-subtitle">{t("home.whatWeSell.description")}</p>
        <WhatWeSellCarousel
          items={PRODUCT_ICONS.map((icon, i) => {
            const text = t(`home.whatWeSell.items.${i}`);
            const colonIdx = text.indexOf(": ");
            return {
              icon,
              title: colonIdx > -1 ? text.slice(0, colonIdx) : text,
              desc:  colonIdx > -1 ? text.slice(colonIdx + 2) : "",
            };
          })}
        />
      </section>

      <div className="home-mv">
        <div className="home-mv-card">
          <h2>{t("home.mission.title")}</h2>
          <p>{t("home.mission.description")}</p>
        </div>
        <div className="home-mv-card">
          <h2>{t("home.vision.title")}</h2>
          <p>{t("home.vision.description")}</p>
        </div>
      </div>

      <section className="home-values">
        <h2 className="home-section-title">{t("home.values.title")}</h2>
        <div className="home-values-grid">
          {VALUE_ICONS.map((icon, i) => {
            const text = t(`home.values.items.${i}`);
            const sepIdx = text.indexOf(" – ");
            const title = sepIdx > -1 ? text.slice(0, sepIdx) : text;
            const desc = sepIdx > -1 ? text.slice(sepIdx + 3) : "";
            return (
              <div className="home-value-card" key={i}>
                <span className="material-symbols-outlined home-value-icon">{icon}</span>
                <p className="home-value-title">{title}</p>
                {desc && <p className="home-value-desc">{desc}</p>}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default Home;
